import React, { useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Space,
  Card,
  Typography,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addAdministratorLead, updateAdministratorLead } from '../../api/administratorLeads';
import type { AdministratorLead } from '../../types/administratorLeads';
import type { Contact } from '../../types/common';
import { getFullName } from '../../utils/text';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

interface AdministratorLeadModalProps {
  open: boolean;
  lead?: AdministratorLead | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const AdministratorLeadModal: React.FC<AdministratorLeadModalProps> = ({
  open,
  lead,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation(['sales', 'common']);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const isEditing = !!lead;

  // Add/Update mutation
  const addMutation = useMutation({
    mutationFn: addAdministratorLead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['administratorLeads'] });
      onSuccess();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AdministratorLead> }) =>
      updateAdministratorLead(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['administratorLeads'] });
      onSuccess();
    },
  });

  useEffect(() => {
    if (open) {
      if (lead) {
        form.setFieldsValue({
          name: getFullName(lead.name),
          status: lead.status,
          source: lead.source,
          address: lead.address,
          city: lead.city,
          pincode: lead.pincode,
          country: lead.country,
          description: lead.description,
          notes: lead.notes,
          contacts: lead.contacts.map((contact: Contact) => ({
            name: contact.name,
            position: contact.position,
            email: contact.email,
            phone: contact.phone,
            type: contact.type,
          })),
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          status: 'New',
          source: 'MINVU Condominios',
          country: 'Chile',
        });
      }
    }
  }, [open, lead, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const administratorLeadData: Omit<AdministratorLead, 'id'> = {
        name: values.name,
        status: values.status,
        source: values.source,
        address: values.address,
        city: values.city,
        pincode: values.pincode,
        country: values.country,
        description: values.description,
        notes: values.notes,
        contacts: values.contacts.map((contact: any, index: number) => ({
          id: `contact-${index}`,
          type: contact.type,
          value: contact.type === 'email' ? contact.email : contact.phone,
          name: contact.name,
          position: contact.position,
          email: contact.email,
          phone: contact.phone,
        })),
        created_at: lead?.created_at || new Date(),
        updated_at: new Date(),
        activities: lead?.activities || [],
      };

      if (isEditing && lead) {
        updateMutation.mutate({ id: lead.id, data: administratorLeadData });
      } else {
        addMutation.mutate(administratorLeadData);
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const isLoading = addMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      title={isEditing ? t('sales:administratorLeads.edit') : t('sales:administratorLeads.add')}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={800}
      confirmLoading={isLoading}
      okText={isEditing ? t('common:update') : t('common:create')}
      cancelText={t('common:cancel')}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'New',
          source: 'MINVU Condominios',
          country: 'Chile',
          contacts: [{ type: 'email' }],
        }}
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Title level={5}>Basic Information</Title>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Form.Item
              name="name"
              label={t('sales:administratorLeads.form.name')}
              rules={[{ required: true, message: t('sales:administratorLeads.form.nameRequired') }]}
            >
              <Input placeholder={t('sales:administratorLeads.form.namePlaceholder')} />
            </Form.Item>

            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="status"
                label={t('sales:administratorLeads.form.status')}
                rules={[{ required: true, message: t('sales:administratorLeads.form.statusRequired') }]}
                style={{ width: '50%', marginRight: 8 }}
              >
                <Select>
                  <Option value="New">{t('sales:administratorLeads.status.new')}</Option>
                  <Option value="Contacted">{t('sales:administratorLeads.status.contacted')}</Option>
                  <Option value="Lost">{t('sales:administratorLeads.status.lost')}</Option>
                  <Option value="Disqualified">{t('sales:administratorLeads.status.disqualified')}</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="source"
                label={t('sales:administratorLeads.form.source')}
                rules={[{ required: true, message: t('sales:administratorLeads.form.sourceRequired') }]}
                style={{ width: '50%' }}
              >
                <Select>
                  <Option value="MINVU Condominios">MINVU Condominios</Option>
                  <Option value="Website">Website</Option>
                  <Option value="Referral">Referral</Option>
                  <Option value="Social Media">Social Media</Option>
                  <Option value="Google Places">Google Places</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Space.Compact>
          </Space>
        </Card>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Title level={5}>Address Information</Title>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Form.Item
              name="address"
              label={t('sales:administratorLeads.form.address')}
              rules={[{ required: true, message: t('sales:administratorLeads.form.addressRequired') }]}
            >
              <Input placeholder={t('sales:administratorLeads.form.addressPlaceholder')} />
            </Form.Item>

            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="city"
                label={t('sales:administratorLeads.form.city')}
                rules={[{ required: true, message: t('sales:administratorLeads.form.cityRequired') }]}
                style={{ width: '33%', marginRight: 8 }}
              >
                <Input placeholder={t('sales:administratorLeads.form.cityPlaceholder')} />
              </Form.Item>

              <Form.Item
                name="pincode"
                label={t('sales:administratorLeads.form.pincode')}
                rules={[{ required: true, message: t('sales:administratorLeads.form.pincodeRequired') }]}
                style={{ width: '33%', marginRight: 8 }}
              >
                <Input placeholder={t('sales:administratorLeads.form.pincodePlaceholder')} />
              </Form.Item>

              <Form.Item
                name="country"
                label={t('sales:administratorLeads.form.country')}
                rules={[{ required: true, message: t('sales:administratorLeads.form.countryRequired') }]}
                style={{ width: '33%' }}
              >
                <Input placeholder={t('sales:administratorLeads.form.countryPlaceholder')} />
              </Form.Item>
            </Space.Compact>
          </Space>
        </Card>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Title level={5}>Contact Information</Title>
          <Form.List name="contacts">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" style={{ marginBottom: 8 }}>
                    <Space.Compact style={{ width: '100%' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'type']}
                        label={t('sales:administratorLeads.form.contactType')}
                        rules={[{ required: true }]}
                        style={{ width: '20%', marginRight: 8 }}
                      >
                        <Select>
                          <Option value="email">Email</Option>
                          <Option value="phone">Phone</Option>
                        </Select>
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        label={t('sales:administratorLeads.form.contactName')}
                        rules={[{ required: true }]}
                        style={{ width: '30%', marginRight: 8 }}
                      >
                        <Input placeholder={t('sales:administratorLeads.form.contactNamePlaceholder')} />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'position']}
                        label={t('sales:administratorLeads.form.contactPosition')}
                        style={{ width: '25%', marginRight: 8 }}
                      >
                        <Input placeholder="Position" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'email']}
                        label={t('sales:administratorLeads.form.contactEmail')}
                        style={{ width: '25%', marginRight: 8 }}
                      >
                        <Input placeholder="Email" type="email" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'phone']}
                        label={t('sales:administratorLeads.form.contactPhone')}
                        style={{ width: '25%', marginRight: 8 }}
                      >
                        <Input placeholder="Phone" />
                      </Form.Item>

                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        style={{ marginTop: 30 }}
                      />
                    </Space.Compact>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  {t('sales:administratorLeads.form.addContact')}
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Card size="small">
          <Title level={5}>Additional Information</Title>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Form.Item
              name="description"
              label={t('sales:administratorLeads.form.description')}
            >
              <TextArea
                rows={3}
                placeholder={t('sales:administratorLeads.form.descriptionPlaceholder')}
              />
            </Form.Item>

            <Form.Item
              name="notes"
              label={t('sales:administratorLeads.form.notes')}
            >
              <TextArea
                rows={3}
                placeholder={t('sales:administratorLeads.form.notesPlaceholder')}
              />
            </Form.Item>
          </Space>
        </Card>
      </Form>
    </Modal>
  );
};

export default AdministratorLeadModal;
